import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { CAT_LABELS, DEST_LABELS, STATUS_LABELS_TOUR, TYPE_LABELS } from "@/pages/admin/AdminTours";

const LANGS = [["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"]];
const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const tcls = "w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const TRI = { yes: "Sì, confermato", no: "No", unconfirmed: "Da confermare (non mostrato come incluso)" };
const TICKET = { yes: "Incluso", no: "Non incluso", partial: "Incluso in parte", unconfirmed: "Da confermare" };

const lines = (arr) => (arr || []).join("\n");
const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const eurosToCents = (v) => Math.round(Number(String(v).replace(",", ".")) * 100);
const centsToEuros = (c) => (c === null || c === undefined ? "" : (Number(c) / 100).toString());
const minutesToHours = (m) => (m === null || m === undefined ? "" : (Number(m) / 60).toString());
const hoursToMinutes = (h) => Math.round(Number(String(h).replace(",", ".")) * 60);

const emptyTr = () => ({ title: "", short: "", description: "", itinerary: "", included: "", not_included: "", good_to_know: "", faqs: "" });

const toForm = (tour) => {
  const tr = {};
  LANGS.forEach(([l]) => {
    const x = tour?.translations?.[l] || {};
    tr[l] = {
      title: x.title || "",
      short: x.short || "",
      description: x.description || "",
      itinerary: (x.itinerary || []).map((s) => `${s.time} | ${s.title} | ${s.text}`).join("\n"),
      included: lines(x.included),
      not_included: lines(x.not_included),
      good_to_know: lines(x.good_to_know),
      faqs: (x.faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n"),
    };
  });
  const p = tour?.pricing || { mode: "per_person", base_cents: Math.round((tour?.price ?? 50) * 100), base_participants: 1, extra_person_cents: null, price_status: "approved" };
  const d = tour?.duration || { min_minutes: Math.round((tour?.duration_hours ?? 3) * 60), max_minutes: Math.round((tour?.duration_hours ?? 3) * 60) };
  const cap = tour?.capacity ? tour.capacity.max_participants : tour?.max_guests ?? null;
  return {
    slug: tour?.slug || "",
    category: tour?.category || "archaeology",
    destination: tour?.destination || "pompei",
    tour_type: tour?.tour_type || "group",
    status: tour?.status || (tour?.active === false ? "draft" : "published"),
    booking_mode: tour?.booking_mode || "instant",
    image: tour?.image || "",
    gallery: lines(tour?.gallery),
    mode: p.mode,
    base_eur: centsToEuros(p.base_cents),
    base_participants: p.base_participants ?? 1,
    extra_eur: centsToEuros(p.extra_person_cents),
    price_status: p.price_status || "approved",
    min_h: minutesToHours(d.min_minutes),
    max_h: minutesToHours(d.max_minutes),
    max_participants: cap ?? "",
    languages: tour?.languages || ["it", "en", "fr", "es"],
    timeslots: (tour?.timeslots || []).join(", "),
    skip_the_line: tour?.skip_the_line || "unconfirmed",
    ticket_included: tour?.ticket_included || "unconfirmed",
    meeting_point: tour?.meeting_point || "",
    meeting_point_status: tour?.meeting_point_status || "pending",
    internal_notes: tour?.internal_notes || "",
    featured: tour?.featured ?? false,
    order: tour?.order ?? 0,
    lat: tour?.lat ?? "",
    lng: tour?.lng ?? "",
    tr,
  };
};

const toPayload = (f) => {
  const translations = {};
  LANGS.forEach(([l]) => {
    const x = f.tr[l] || emptyTr();
    translations[l] = {
      title: x.title.trim(),
      short: x.short.trim(),
      description: x.description.trim(),
      itinerary: toList(x.itinerary).map((row) => {
        const [time = "", title = "", ...rest] = row.split("|").map((p) => p.trim());
        return { time, title, text: rest.join(" | ") };
      }),
      included: toList(x.included),
      not_included: toList(x.not_included),
      good_to_know: toList(x.good_to_know),
      faqs: toList(x.faqs).map((row) => {
        const [q = "", ...a] = row.split("|").map((p) => p.trim());
        return { q, a: a.join(" | ") };
      }),
    };
  });
  const minM = hoursToMinutes(f.min_h || f.max_h);
  const maxM = hoursToMinutes(f.max_h || f.min_h);
  return {
    slug: f.slug.trim().toLowerCase(),
    category: f.category,
    destination: f.destination || "",
    tour_type: f.tour_type,
    status: f.status,
    booking_mode: f.booking_mode,
    image: f.image.trim(),
    gallery: toList(f.gallery),
    pricing: {
      mode: f.mode,
      currency: "EUR",
      base_cents: eurosToCents(f.base_eur || 0),
      base_participants: f.mode === "per_group" ? Number(f.base_participants) || 1 : 1,
      extra_person_cents: f.mode === "per_group" && String(f.extra_eur).trim() !== "" ? eurosToCents(f.extra_eur) : null,
      price_status: f.price_status,
    },
    duration: { min_minutes: Math.min(minM, maxM), max_minutes: Math.max(minM, maxM) },
    capacity: { max_participants: String(f.max_participants).trim() === "" ? null : Number(f.max_participants), min_participants: 1 },
    languages: f.languages,
    timeslots: f.timeslots.split(",").map((s) => s.trim()).filter(Boolean),
    skip_the_line: f.skip_the_line,
    ticket_included: f.ticket_included,
    meeting_point: f.meeting_point.trim(),
    meeting_point_status: f.meeting_point_status,
    internal_notes: f.internal_notes,
    featured: f.featured,
    order: Number(f.order) || 0,
    lat: f.lat === "" || f.lat === null ? null : Number(f.lat),
    lng: f.lng === "" || f.lng === null ? null : Number(f.lng),
    translations,
  };
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 70);

const F = ({ label, hint, children, className = "" }) => (
  <div className={className}>
    <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-ocean/45 mt-1">{hint}</p>}
  </div>
);

const Sel = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const Section = ({ title, sub, children }) => (
  <div className="mt-8">
    <h3 className="font-serif text-lg text-ocean">{title}</h3>
    {sub && <p className="text-xs text-ocean/55 mt-1 mb-4">{sub}</p>}
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </div>
);

export const TourEditor = ({ open, tour, onClose, onSaved, nextOrder }) => {
  const [f, setF] = useState(toForm(null));
  const [saving, setSaving] = useState(false);
  const isNew = !tour;

  useEffect(() => {
    if (open) {
      const base = toForm(tour);
      if (!tour) base.order = nextOrder;
      setF(base);
    }
  }, [open, tour, nextOrder]);

  const setTr = (l, k, v) => setF((p) => {
    const next = { ...p, tr: { ...p.tr, [l]: { ...p.tr[l], [k]: v } } };
    if (isNew && l === "en" && k === "title") next.slug = slugify(v);
    return next;
  });

  const toggleLang = (l) => setF((p) => ({ ...p, languages: p.languages.includes(l) ? p.languages.filter((x) => x !== l) : [...p.languages, l] }));

  const save = async () => {
    if (!f.tr.en.title.trim()) return toast.error("Il titolo in inglese è obbligatorio");
    if (!f.image.trim()) return toast.error("Inserisci l'URL dell'immagine principale");
    if (!f.languages.length) return toast.error("Seleziona almeno una lingua di conduzione");
    if (String(f.base_eur).trim() === "") return toast.error("Inserisci il prezzo");
    setSaving(true);
    try {
      const payload = toPayload(f);
      if (isNew) await adminApi.post("/admin/tours", payload);
      else await adminApi.put(`/admin/tours/${tour.id}`, payload);
      toast.success(isNew ? "Tour creato" : "Tour salvato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const perGroup = f.mode === "per_group";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl bg-white overflow-y-auto" data-testid="tour-editor">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-ocean">{isNew ? "Nuovo tour" : "Modifica tour"}</SheetTitle>
          <SheetDescription>I campi in inglese sono la base: se una lingua è vuota, il sito mostra l'inglese. Non inserire dati non confermati come certi.</SheetDescription>
        </SheetHeader>

        <Section title="Prodotto" sub="Identità, stato di pubblicazione e classificazione.">
          <F label="Slug (indirizzo web)" hint={`/tours/${f.slug || "..."}`}><input className={cls} value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="tour-editor-slug" /></F>
          <F label="Stato"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS_LABELS_TOUR} testId="tour-editor-status" /></F>
          <F label="Categoria"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CAT_LABELS} testId="tour-editor-category" /></F>
          <F label="Destinazione"><Sel value={f.destination} onChange={(v) => setF({ ...f, destination: v })} options={DEST_LABELS} testId="tour-editor-destination" /></F>
          <F label="Tipo di tour"><Sel value={f.tour_type} onChange={(v) => setF({ ...f, tour_type: v })} options={TYPE_LABELS} testId="tour-editor-type" /></F>
          <F label="Modalità di prenotazione" hint="Su richiesta: il cliente non può prenotare online, solo inviare una richiesta.">
            <Sel value={f.booking_mode} onChange={(v) => setF({ ...f, booking_mode: v })} options={{ instant: "Prenotazione online (conferma del team)", request: "Solo su richiesta" }} testId="tour-editor-booking-mode" />
          </F>
        </Section>

        <Section title="Prezzo" sub="Importi in euro, salvati in centesimi. Per i tour privati il prezzo copre il gruppo base: se il supplemento per persona aggiuntiva è vuoto, oltre la base il cliente riceve un modulo di preventivo.">
          <F label="Prezzo riferito a"><Sel value={f.mode} onChange={(v) => setF({ ...f, mode: v })} options={{ per_person: "A persona", per_group: "A gruppo (privato)" }} testId="tour-editor-pricing-mode" /></F>
          <F label={perGroup ? "Prezzo del gruppo (€)" : "Prezzo a persona (€)"}><input type="number" min="0" step="0.01" className={cls} value={f.base_eur} onChange={(e) => setF({ ...f, base_eur: e.target.value })} data-testid="tour-editor-price" /></F>
          {perGroup && (
            <>
              <F label="Persone incluse nel prezzo base"><input type="number" min="1" max="30" className={cls} value={f.base_participants} onChange={(e) => setF({ ...f, base_participants: e.target.value })} data-testid="tour-editor-base-participants" /></F>
              <F label="Supplemento per persona aggiuntiva (€)" hint="Lascia vuoto se non definito: non verrà inventato."><input type="number" min="0" step="0.01" className={cls} value={f.extra_eur} onChange={(e) => setF({ ...f, extra_eur: e.target.value })} data-testid="tour-editor-extra-person" /></F>
            </>
          )}
          <F label="Stato del prezzo" hint="Bozza: il tour non è prenotabile online."><Sel value={f.price_status} onChange={(v) => setF({ ...f, price_status: v })} options={{ approved: "Approvato", draft: "Bozza / da approvare" }} testId="tour-editor-price-status" /></F>
        </Section>

        <Section title="Durata, capienza e orari">
          <div className="grid grid-cols-2 gap-4">
            <F label="Durata min (ore)"><input type="number" min="0.5" step="0.5" className={cls} value={f.min_h} onChange={(e) => setF({ ...f, min_h: e.target.value })} data-testid="tour-editor-duration-min" /></F>
            <F label="Durata max (ore)"><input type="number" min="0.5" step="0.5" className={cls} value={f.max_h} onChange={(e) => setF({ ...f, max_h: e.target.value })} data-testid="tour-editor-duration-max" /></F>
          </div>
          <F label="Partecipanti massimi" hint="Vuoto = non definito: il calendario non mostra posti e la conferma è manuale."><input type="number" min="1" max="60" className={cls} value={f.max_participants} onChange={(e) => setF({ ...f, max_participants: e.target.value })} data-testid="tour-editor-max-guests" /></F>
          <F label="Orari di partenza" hint="Separati da virgola (es. 09:00, 14:30). Vuoto = a scelta del cliente."><input className={cls} value={f.timeslots} onChange={(e) => setF({ ...f, timeslots: e.target.value })} data-testid="tour-editor-timeslots" /></F>
          <F label="Lingue di conduzione">
            <div className="flex flex-wrap gap-4 h-11 items-center">
              {LANGS.map(([l, n]) => (
                <label key={l} className="flex items-center gap-2 text-sm text-ocean"><Checkbox checked={f.languages.includes(l)} onCheckedChange={() => toggleLang(l)} data-testid={`tour-editor-lang-${l}`} />{n}</label>
              ))}
            </div>
          </F>
        </Section>

        <Section title="Inclusioni e punto d'incontro" sub="Le voci «da confermare» non vengono mai mostrate al cliente come incluse.">
          <F label="Salta la fila / ingresso prioritario"><Sel value={f.skip_the_line} onChange={(v) => setF({ ...f, skip_the_line: v })} options={TRI} testId="tour-editor-skipline" /></F>
          <F label="Biglietto d'ingresso"><Sel value={f.ticket_included} onChange={(v) => setF({ ...f, ticket_included: v })} options={TICKET} testId="tour-editor-ticket" /></F>
          <F label="Punto d'incontro"><input className={cls} value={f.meeting_point} onChange={(e) => setF({ ...f, meeting_point: e.target.value })} data-testid="tour-editor-meeting" /></F>
          <F label="Stato punto d'incontro" hint="Se «da confermare», il sito avvisa che l'indirizzo esatto arriva con la conferma."><Sel value={f.meeting_point_status} onChange={(v) => setF({ ...f, meeting_point_status: v })} options={{ pending: "Da confermare", confirmed: "Confermato" }} testId="tour-editor-meeting-status" /></F>
          <div className="grid grid-cols-2 gap-4 md:col-span-2">
            <F label="Latitudine (mappa)" hint="Es. 40.7490 · da Google Maps, clic destro sul luogo"><input type="number" step="0.0001" className={cls} value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} data-testid="tour-editor-lat" /></F>
            <F label="Longitudine (mappa)" hint="Es. 14.4850"><input type="number" step="0.0001" className={cls} value={f.lng} onChange={(e) => setF({ ...f, lng: e.target.value })} data-testid="tour-editor-lng" /></F>
          </div>
        </Section>

        <Section title="Immagini e visibilità">
          <F label="Immagine principale (URL)" className="md:col-span-2">
            <div className="flex gap-3 items-center">
              <input className={cls} value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="tour-editor-image" />
              {f.image && <img src={f.image} alt="" className="w-16 h-11 rounded-lg object-cover bg-sand shrink-0" />}
            </div>
          </F>
          <F label="Galleria (un URL per riga)" className="md:col-span-2"><textarea rows={2} className={tcls} value={f.gallery} onChange={(e) => setF({ ...f, gallery: e.target.value })} data-testid="tour-editor-gallery" /></F>
          <F label="Ordine"><input type="number" className={cls} value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="tour-editor-order" /></F>
          <div className="flex items-center gap-3 h-11 self-end">
            <label className="flex items-center gap-3 text-sm text-ocean"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="tour-editor-featured" />In evidenza in home</label>
          </div>
          <F label="Note interne (non pubbliche)" className="md:col-span-2"><textarea rows={2} className={tcls} value={f.internal_notes} onChange={(e) => setF({ ...f, internal_notes: e.target.value })} data-testid="tour-editor-internal-notes" /></F>
        </Section>

        <Tabs defaultValue="en" className="mt-8">
          <TabsList className="bg-ivory flex-wrap h-auto">
            {LANGS.map(([l, n]) => <TabsTrigger key={l} value={l} data-testid={`tour-editor-tab-${l}`}>{n}{l === "en" ? " *" : ""}</TabsTrigger>)}
          </TabsList>
          {LANGS.map(([l]) => (
            <TabsContent key={l} value={l} className="space-y-5 mt-5">
              <F label="Titolo"><input className={cls} value={f.tr[l]?.title || ""} onChange={(e) => setTr(l, "title", e.target.value)} data-testid={`tour-editor-${l}-title`} /></F>
              <F label="Descrizione breve"><textarea rows={2} className={tcls} value={f.tr[l]?.short || ""} onChange={(e) => setTr(l, "short", e.target.value)} data-testid={`tour-editor-${l}-short`} /></F>
              <F label="Descrizione completa" hint="Lascia una riga vuota tra i paragrafi"><textarea rows={5} className={tcls} value={f.tr[l]?.description || ""} onChange={(e) => setTr(l, "description", e.target.value)} data-testid={`tour-editor-${l}-description`} /></F>
              <F label="Programma della giornata" hint="Una tappa per riga: orario | titolo | testo"><textarea rows={4} className={tcls} value={f.tr[l]?.itinerary || ""} onChange={(e) => setTr(l, "itinerary", e.target.value)} data-testid={`tour-editor-${l}-itinerary`} /></F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Incluso" hint="Una voce per riga"><textarea rows={4} className={tcls} value={f.tr[l]?.included || ""} onChange={(e) => setTr(l, "included", e.target.value)} data-testid={`tour-editor-${l}-included`} /></F>
                <F label="Non incluso" hint="Una voce per riga"><textarea rows={4} className={tcls} value={f.tr[l]?.not_included || ""} onChange={(e) => setTr(l, "not_included", e.target.value)} data-testid={`tour-editor-${l}-not-included`} /></F>
              </div>
              <F label="Da sapere" hint="Una voce per riga"><textarea rows={3} className={tcls} value={f.tr[l]?.good_to_know || ""} onChange={(e) => setTr(l, "good_to_know", e.target.value)} data-testid={`tour-editor-${l}-good-to-know`} /></F>
              <F label="Domande frequenti" hint="Una per riga: domanda | risposta"><textarea rows={3} className={tcls} value={f.tr[l]?.faqs || ""} onChange={(e) => setTr(l, "faqs", e.target.value)} data-testid={`tour-editor-${l}-faqs`} /></F>
            </TabsContent>
          ))}
        </Tabs>

        <div className="sticky bottom-0 bg-white pt-5 pb-2 mt-8 border-t border-sand flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean" data-testid="tour-editor-cancel">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="tour-editor-save">{saving ? "Salvataggio..." : "Salva tour"}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
